import type { Microsoft365TeamAdminDto, Microsoft365TeamDto } from '@dbrain/shared';
import { EmptyState, Icons, Skeleton, Switch } from '@dbrain/ui';
import { useId } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMicrosoft365TeamSwitch, useMicrosoft365Teams } from '../../../api/microsoft365-admin.js';
import { ErrorAlert } from '../../ErrorAlert.js';
import { teamBlock, teamProducts } from './model.js';
import { BuiltinTile } from './parts.js';

/**
 * Yönetim › Entegrasyonlar for a team admin (MsEkipYoneticisi; D-101 as amended in Phase 15e): Microsoft 365 on
 * or off for each team they lead, within the firm's limit, with how many of the team are connected. Never the
 * firm's connections, the setup or the Microsoft settings; turning a team off keeps its people's accounts. Part 3
 * keeps that one switch: sending, meetings and documents are the firm's switches, named in the card's lead.
 */
export function Microsoft365TeamAdmin() {
  const { t, i18n } = useTranslation();
  const teams = useMicrosoft365Teams();
  const toggle = useMicrosoft365TeamSwitch();
  const dto = teams.data;
  const names = dto ? new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(dto.teams.map((team) => team.name)) : '';
  const pill = dto ? (!dto.configured ? 'notSetUp' : dto.enabled ? 'firmOn' : 'firmOff') : null;
  const lead = teamProducts(dto?.features ?? { mail_read: true, calendar_read: true, mail_send: false, calendar_write: false, onedrive_import: false, sharepoint_import: false });
  return (
    <div className="m365a-admin m365a-team-admin" data-testid="m365-team-admin">
      {dto && dto.teams.length > 0 ? (
        <p className="admin-info" data-testid="m365-team-note">
          <Icons.info size={16} />
          <span>
            <Trans i18nKey="microsoft365.admin.team.note" count={dto.teams.length} values={{ teams: names }} components={{ b: <strong /> }} />
          </span>
        </p>
      ) : null}
      <ErrorAlert error={teams.error ?? toggle.error} />
      {teams.isPending ? <Skeleton block /> : null}
      {dto && pill ? (
        <section className="admin-card m365a-team-card" data-testid="m365-team-card" data-status={pill}>
          <header className="m365a-team-card__head">
            <BuiltinTile />
            <div className="m365a-card__main">
              <div className="m365a-card__title">
                <h2>{t('microsoft365.admin.name')}</h2>
                <span className={`connector-state${pill === 'firmOn' ? ' connector-state--ready' : ''}`} data-testid="m365-team-firm">
                  {t(`microsoft365.admin.team.${pill}`)}
                </span>
              </div>
              <p className="m365a-card__lead" data-testid="m365-team-products">
                {/* MsEkipYoneticisi: "Outlook mail ve takvim, OneDrive, SharePoint"; "yalnız okuma" while nothing can be sent */}
                {lead.keys.map((key) => t(`microsoft365.admin.products.${key}`)).join(', ')}
                {lead.readOnly ? ` · ${t('microsoft365.admin.readOnly')}` : ''}
              </p>
            </div>
          </header>
          {!dto.configured ? (
            <p className="m365a-team-card__text">{t('microsoft365.admin.team.notSetUpText')}</p>
          ) : dto.teams.length === 0 ? (
            <EmptyState>{t('microsoft365.admin.team.none')}</EmptyState>
          ) : (
            dto.teams.map((team) => <TeamRow key={team.id} dto={dto} team={team} busy={toggle.isPending} onChange={(enabled) => toggle.mutate({ teamId: team.id, enabled })} />)
          )}
        </section>
      ) : null}
    </div>
  );
}

/** "Pazarlama ekibi için · Açık" and "Ekibinizde bağlı: 6 / 9 kişi"; a team the firm keeps it from shows why. */
function TeamRow({ dto, team, busy, onChange }: { dto: Microsoft365TeamAdminDto; team: Microsoft365TeamDto; busy: boolean; onChange: (enabled: boolean) => void }) {
  const { t } = useTranslation();
  const id = useId();
  const blocked = teamBlock(dto, team);
  // a switch the firm holds shows off: what counts is that no one in the team can use it now
  const on = blocked === null && team.teamEnabled;
  const hint = blocked ? t(`microsoft365.admin.team.blocked.${blocked}`) : t(on ? 'microsoft365.admin.team.offHint' : 'microsoft365.admin.team.onHint', { count: team.members });
  return (
    <div className="m365a-team" data-testid="m365-team" data-name={team.name} data-on={on ? 'true' : 'false'}>
      <div className="m365a-team__row">
        <span className="m365a-team__text">
          <label htmlFor={id} className="m365a-team__name">
            {t('microsoft365.admin.team.for', { team: team.name })}
          </label>
          <span id={`${id}-hint`} className="m365a-team__hint">
            {hint}
          </span>
        </span>
        <span className="m365a-team__state" aria-hidden="true">
          {t(on ? 'microsoft365.admin.team.on' : 'microsoft365.admin.team.off')}
        </span>
        <Switch
          id={id}
          aria-describedby={`${id}-hint`}
          checked={on}
          disabled={blocked !== null || busy}
          onChange={(e) => onChange(e.target.checked)}
          data-testid="m365-team-switch"
        />
      </div>
      <p className="m365a-team__count" data-testid="m365-team-count">
        {t('microsoft365.admin.team.connected', { connected: team.connected, members: team.members })}
      </p>
    </div>
  );
}
